import type { IPlatformStats } from '../redux/buzzbridge/types';

/** Manual platform stats entered by the creator (last-post metrics). */
export interface IManualPlatformLinkData {
    userName: string;
    followers: string;
    lastPostViews: string;
    lastPostComments: string;
    lastPostLikes: string;
    /** Cropped preview (data URL or existing CDN URL). */
    headshotPreview?: string;
    /** Raw file — uploaded via signed-URL flow on save. */
    headshotFile?: File;
}

export const EMPTY_MANUAL_PLATFORM_LINK: IManualPlatformLinkData = {
    userName: '',
    followers: '',
    lastPostViews: '',
    lastPostComments: '',
    lastPostLikes: '',
};

export interface IManualPlatformLinkCompleteOptions {
    /** Counts toward headshot when user already has a profile picture on file. */
    existingHeadshotUrl?: string;
}

export function hasManualLinkHeadshot(
    data: IManualPlatformLinkData | undefined,
    options?: IManualPlatformLinkCompleteOptions,
): boolean {
    if (!data) return false;
    if (data.headshotFile) return true;
    if (data.headshotPreview !== undefined) return Boolean(data.headshotPreview.trim());
    return Boolean(options?.existingHeadshotUrl?.trim());
}

/** Strip non-serializable fields before sending manual data to the API. */
export function stripManualLinkForApi(data: IManualPlatformLinkData) {
    const { headshotFile: _file, headshotPreview: _preview, ...rest } = data;
    return rest;
}

const parseCount = (value: string | number | undefined): number => {
    const n = Number(
        String(value ?? '')
            .replace(/,/g, '')
            .trim(),
    );
    return Number.isFinite(n) && n >= 0 ? n : NaN;
};

/**
 * Engagement rate (%) from last-post metrics: (likes + comments) ÷ followers × 100.
 * Matches how brands typically verify post-level engagement.
 */
export function calculateManualEngagementRatePercent(
    followers: string | number | undefined,
    likes: string | number | undefined,
    comments: string | number | undefined,
): number | null {
    const followerCount = parseCount(followers);
    const likeCount = parseCount(likes);
    const commentCount = parseCount(comments);

    if (!followerCount || followerCount <= 0) return null;
    if (Number.isNaN(likeCount) || Number.isNaN(commentCount)) return null;

    return ((likeCount + commentCount) / followerCount) * 100;
}

export function formatManualEngagementRate(
    followers: string | number | undefined,
    likes: string | number | undefined,
    comments: string | number | undefined,
): string {
    const rate = calculateManualEngagementRatePercent(followers, likes, comments);
    if (rate === null) return '—';
    return `${rate.toFixed(2)}%`;
}

export function isManualPlatformLinkComplete(
    data: IManualPlatformLinkData | undefined,
    options?: IManualPlatformLinkCompleteOptions,
): boolean {
    if (!data) return false;
    return (
        Boolean(data.userName.trim()) &&
        Boolean(data.followers.trim()) &&
        Boolean(data.lastPostViews.trim()) &&
        Boolean(data.lastPostComments.trim()) &&
        Boolean(data.lastPostLikes.trim()) &&
        hasManualLinkHeadshot(data, options)
    );
}

/** Maps manual entry to influencer platform stats (engagement stored as decimal, e.g. 0.035 = 3.5%). */
export function manualPlatformLinkToStats(data: IManualPlatformLinkData): IPlatformStats {
    const engagementPercent = calculateManualEngagementRatePercent(
        data.followers,
        data.lastPostLikes,
        data.lastPostComments,
    );

    return {
        username: data.userName.trim(),
        followers: parseCount(data.followers) || 0,
        avg_likes: parseCount(data.lastPostLikes) || 0,
        avg_comments: parseCount(data.lastPostComments) || 0,
        engagementRate: engagementPercent != null ? engagementPercent / 100 : 0,
        verified: false,
    };
}

/** Hydrate form from saved platform stats (views are not persisted — user re-enters if needed). */
export function manualPlatformLinkFromStats(stats: IPlatformStats | undefined): IManualPlatformLinkData {
    if (!stats) return { ...EMPTY_MANUAL_PLATFORM_LINK };
    return {
        userName: stats.username ?? '',
        followers: stats.followers != null ? String(stats.followers) : '',
        lastPostViews: '',
        lastPostComments: stats.avg_comments != null ? String(stats.avg_comments) : '',
        lastPostLikes: stats.avg_likes != null ? String(stats.avg_likes) : '',
    };
}
